import { useMutation, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Bug, CheckCircle2, ImagePlus, Lightbulb } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";

import { api, ApiError } from "../api/client";
import type { FeedbackContext, FeedbackItem, FeedbackKind, FeedbackSeverity } from "../api/types";
import { designate } from "../components/attention";
import { Callout } from "../components/Callout";
import { Checkbox } from "../components/Checkbox";
import { Dialog, DialogBody } from "../components/Dialog";
import { Disclosure, DisclosureChevron, PropRow } from "../components/panel";
import { Button, Field, inputClass, Segmented, textareaClass } from "../components/ui";
import { viewLabel, VIEW_META } from "../components/viewMeta";
import { Waiting } from "../components/Waiting";
import { t, tServer } from "../i18n/i18n";
import { missionName } from "../i18n/vocabulary";
import { levelLabel } from "../missions";
import type { ViewKey } from "../state/ui";
import { imageFileToDataUrl } from "./capture";
import {
  contextRows,
  DESCRIPTION_MAX,
  DETAIL_MAX,
  type DraftField,
  draftDirty,
  FEEDBACK_KEY,
  type FeedbackDraft,
  type FeedbackPrefill,
  KIND_LABELS,
  missingField,
  openFeedbackPane,
  SEVERITY_LABELS,
  TITLE_MAX,
} from "./feedbackModel";

/** Libellé d'une vue du contexte (clé de `ViewKey`), sinon la clé telle quelle. */
export function contextViewLabel(view: string): string {
  return view in VIEW_META ? viewLabel(view as ViewKey) : view;
}

export const CONTEXT_LABELS = { view: contextViewLabel, level: (l: string) => levelLabel(l), mission: (m: string) => missionName(m) };

/** Capture : en cours (`pending`), prête (image `data:`), indisponible (`null`). */
type Shot = "pending" | string | null;

/** Fenêtre « Suggérer une évolution » / « Signaler une anomalie » (registre F39). Le type se change en tête ; une
 *  anomalie demande ce que faisait l'utilisateur, ce qui s'est passé et sa gravité. La capture de la vue (prise à
 *  l'ouverture, avant la fenêtre) est montrée et jointe si la case est cochée — cochée d'office pour une anomalie ;
 *  une image peut la remplacer. Le contexte technique joint est montré tel qu'il part. Un champ requis manquant est
 *  désigné à l'appui sur « Envoyer » ; l'envoi montre ses points d'attente ; la fenêtre confirme ensuite le numéro
 *  du retour. */
export function FeedbackDialog({
  kind: initialKind,
  capture,
  context,
  prefill = null,
  onClose,
}: {
  kind: FeedbackKind;
  capture: Promise<string | null> | null;
  context: FeedbackContext;
  /** Champs remplis d'avance : « Signaler cette anomalie » d'un message d'erreur reprend son texte. */
  prefill?: FeedbackPrefill | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const prefilled = Boolean(prefill?.happened);
  const [draft, setDraft] = useState<FeedbackDraft>({
    kind: initialKind,
    title: "",
    description: "",
    doing: "",
    happened: prefill?.happened ?? "",
    severity: null,
  });
  const [shot, setShot] = useState<Shot>(capture ? "pending" : null);
  const [attach, setAttach] = useState(initialKind === "anomaly");
  const [attachTouched, setAttachTouched] = useState(false);
  const [unreadable, setUnreadable] = useState(false);
  const [showContext, setShowContext] = useState(false);
  const [sent, setSent] = useState<FeedbackItem | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const happenedRef = useRef<HTMLTextAreaElement>(null);
  const severityRef = useRef<HTMLDivElement>(null);
  const contextId = useId();

  useEffect(() => {
    let alive = true;
    void capture?.then((image) => alive && setShot(image));
    return () => {
      alive = false;
    };
  }, [capture]);

  const update = (patch: Partial<FeedbackDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const changeKind = (kind: FeedbackKind) => {
    update({ kind });
    // Case de la capture : suit le type tant que l'utilisateur ne l'a pas changée lui-même.
    if (!attachTouched) setAttach(kind === "anomaly");
  };

  const send = useMutation({
    mutationFn: () =>
      api.feedback.send({
        kind: draft.kind,
        title: draft.title.trim(),
        description: draft.description.trim(),
        doing: draft.kind === "anomaly" ? draft.doing.trim() || null : null,
        happened: draft.kind === "anomaly" ? draft.happened.trim() || null : null,
        severity: draft.kind === "anomaly" ? draft.severity : null,
        context,
        screenshot: attach && typeof shot === "string" && shot !== "pending" ? shot : null,
      }),
    onSuccess: (item) => {
      setSent(item);
      void queryClient.invalidateQueries({ queryKey: FEEDBACK_KEY });
    },
  });

  const missing = missingField(draft);
  const targets: Record<DraftField, () => HTMLElement | null> = {
    title: () => titleRef.current,
    description: () => descriptionRef.current,
    happened: () => happenedRef.current,
    severity: () => severityRef.current,
  };
  const pickImage = async (file: File | undefined) => {
    if (!file) return;
    const image = await imageFileToDataUrl(file);
    setUnreadable(!image);
    if (image) {
      setShot(image);
      setAttach(true);
      setAttachTouched(true);
    }
  };

  if (sent) {
    return (
      <Dialog
        title={t("Retour envoyé")}
        onClose={onClose}
        width={480}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                onClose();
                openFeedbackPane("mine");
              }}
            >
              {t("Voir mes retours")}
            </Button>
            <Button onClick={onClose} autoFocus>
              {t("Fermer")}
            </Button>
          </>
        }
      >
        <DialogBody>
          <div className="flex items-start gap-3">
            <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-ok" aria-hidden />
            <div className="space-y-1">
              <p className="text-title font-semibold text-ink">{t("Merci : retour n° {n} transmis à l'équipe Generex.", { n: sent.number })}</p>
              <p className="text-ui leading-relaxed text-ink-soft">
                {t("Vous suivrez son état et la réponse de l'équipe dans « Mes retours », depuis le bouton « Retours ».")}
              </p>
            </div>
          </div>
        </DialogBody>
      </Dialog>
    );
  }

  const rows = contextRows(context, CONTEXT_LABELS);
  const anomaly = draft.kind === "anomaly";
  return (
    <Dialog
      title={anomaly ? t("Signaler une anomalie") : t("Suggérer une évolution")}
      subtitle={t("Transmis à l'équipe Generex, qui vous répond dans « Mes retours ».")}
      onClose={onClose}
      width={600}
      // Texte repris d'un message d'erreur et laissé tel quel : rien à confirmer à la fermeture.
      dirty={draftDirty(prefilled && draft.happened === prefill?.happened ? { ...draft, happened: "" } : draft) && !send.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("Annuler")}
          </Button>
          <Button
            blocked={missing !== null || send.isPending}
            onBlocked={() => {
              if (missing) designate(targets[missing](), missing === "severity" ? "ring" : "field");
            }}
            onClick={() => send.mutate()}
          >
            {send.isPending ? <Waiting>{t("Envoi en cours…")}</Waiting> : t("Envoyer")}
          </Button>
        </>
      }
    >
      <DialogBody className="space-y-4">
        <Segmented
          ariaLabel={t("Type de retour")}
          value={draft.kind}
          onChange={changeKind}
          options={[
            { value: "evolution", label: t(KIND_LABELS.evolution), icon: Lightbulb },
            { value: "anomaly", label: t(KIND_LABELS.anomaly), icon: Bug },
          ]}
        />
        <Field label={t("Titre")} hint={anomaly ? t("En quelques mots : « La carte ne se cadre pas sur le camp ».") : t("En quelques mots : « Exporter la 3A en tableur ».")}>
          <input
            ref={titleRef}
            className={inputClass}
            value={draft.title}
            maxLength={TITLE_MAX}
            autoFocus
            onChange={(e) => update({ title: e.target.value })}
          />
        </Field>
        {anomaly ? (
          <>
            <Field label={t("Ce que vous faisiez")} hint={t("Facultatif : la vue, le dossier, le bouton appuyé.")}>
              <textarea
                className={clsx(textareaClass, "min-h-16")}
                value={draft.doing}
                maxLength={DETAIL_MAX}
                onChange={(e) => update({ doing: e.target.value })}
              />
            </Field>
            <Field label={t("Ce qui s'est passé")} hint={prefilled ? t("Repris du message affiché : complétez-le au besoin.") : undefined}>
              <textarea
                ref={happenedRef}
                className={clsx(textareaClass, "min-h-20")}
                value={draft.happened}
                maxLength={DETAIL_MAX}
                onChange={(e) => update({ happened: e.target.value })}
              />
            </Field>
            <div ref={severityRef} className="rounded-lg">
              <Segmented<FeedbackSeverity | "none">
                label={t("Gravité")}
                value={draft.severity ?? "none"}
                onChange={(value) => update({ severity: value === "none" ? null : value })}
                options={[
                  { value: "annoying", label: t(SEVERITY_LABELS.annoying), title: t("Le travail continue, avec une gêne.") },
                  { value: "blocking", label: t(SEVERITY_LABELS.blocking), title: t("Le travail ne peut pas continuer.") },
                ]}
              />
            </div>
            <Field label={t("Précisions")} hint={t("Facultatif : ce que vous attendiez, ce qui se répète.")}>
              <textarea
                ref={descriptionRef}
                className={clsx(textareaClass, "min-h-16")}
                value={draft.description}
                maxLength={DESCRIPTION_MAX}
                onChange={(e) => update({ description: e.target.value })}
              />
            </Field>
          </>
        ) : (
          <Field label={t("Description")} hint={t("Ce que vous voudriez pouvoir faire, et pourquoi.")}>
            <textarea
              ref={descriptionRef}
              className={clsx(textareaClass, "min-h-28")}
              value={draft.description}
              maxLength={DESCRIPTION_MAX}
              onChange={(e) => update({ description: e.target.value })}
            />
          </Field>
        )}

        <section className="space-y-2 rounded-lg border border-line px-3 py-2.5">
          <Checkbox
            checked={attach && shot !== null}
            disabled={shot === null}
            onChange={(on) => {
              setAttach(on);
              setAttachTouched(true);
            }}
            label={t("Joindre une capture de l'écran")}
            detail={t("La vue telle qu'elle était à l'ouverture de cette fenêtre. Vérifiez qu'elle ne montre rien que vous ne souhaitez pas transmettre.")}
          />
          <Disclosure open={attach && shot !== null}>
            <div className="pt-1">
              {shot === "pending" ? (
                <p className="text-meta text-ink-muted">
                  <Waiting>{t("Capture de la vue…")}</Waiting>
                </p>
              ) : typeof shot === "string" ? (
                <img
                  src={shot}
                  alt={t("Capture de l'écran jointe")}
                  className="max-h-56 w-full rounded-md border border-line bg-canvas object-contain"
                />
              ) : null}
            </div>
          </Disclosure>
          {shot === null && <p className="text-meta leading-snug text-ink-muted">{t("La capture de l'écran n'est pas disponible sur ce navigateur : vous pouvez joindre une image.")}</p>}
          <div className="flex flex-wrap items-center gap-2 pt-0.5">
            <Button variant="ghost" size="sm" onClick={() => fileInput.current?.click()}>
              <ImagePlus size={14} /> {typeof shot === "string" && shot !== "pending" ? t("Remplacer par une image…") : t("Joindre une image…")}
            </Button>
            {unreadable && <span className="text-meta text-danger">{t("Image illisible : PNG, JPEG ou WebP, 8 Mo au plus.")}</span>}
          </div>
          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => {
              void pickImage(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
        </section>

        <section className="rounded-lg border border-line">
          <button
            type="button"
            aria-expanded={showContext}
            aria-controls={contextId}
            onClick={() => setShowContext((s) => !s)}
            className="flex min-h-9 w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-ui text-ink-soft transition-colors hover:bg-hover"
          >
            <DisclosureChevron open={showContext} />
            <span className="min-w-0 flex-1">{t("Informations techniques jointes")}</span>
            <span className="text-meta text-ink-muted">{t("Sans licence ni mot de passe")}</span>
          </button>
          <Disclosure open={showContext} id={contextId}>
            <div className="border-t border-line px-3 py-1">
              {rows.map((row) => (
                <PropRow key={row.label} label={row.label}>
                  {row.value}
                </PropRow>
              ))}
            </div>
          </Disclosure>
        </section>

        {send.isError && (
          <Callout tone="danger">
            {send.error instanceof ApiError ? tServer(send.error.message) : t("Le retour n'a pas pu être envoyé : réessayez dans un instant.")}
          </Callout>
        )}
      </DialogBody>
    </Dialog>
  );
}
